import { useState } from "react";
import { useBetSlip } from "@/context/BetSlipContext";
import { useDisplay } from "@/context/DisplayContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Layers, X, Trash2, TrendingUp, Goal } from "lucide-react";

const money = (n) => `A$${n.toFixed(2)}`;

export default function BetSlip() {
  const { legs, removeLeg, clear, stake, setStake, summary } = useBetSlip();
  const { fmtProb } = useDisplay();
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="betslip-fab"
          className="fixed bottom-24 sm:bottom-6 right-4 z-[60] h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Open bet slip"
        >
          <Layers className="h-6 w-6" />
          {legs.length > 0 && (
            <span
              className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-[#FF375F] text-white text-xs font-bold flex items-center justify-center border-2 border-background"
              data-testid="betslip-count"
            >
              {legs.length}
            </span>
          )}
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="betslip-panel">
        <SheetHeader className="p-5 pb-3 border-b border-border">
          <SheetTitle className="font-display flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" /> Multi Bet Builder
          </SheetTitle>
        </SheetHeader>

        {legs.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8" data-testid="betslip-empty">
            <Layers className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="font-semibold">Your slip is empty</p>
            <p className="text-sm text-muted-foreground mt-1">
              Tap "+ Add to slip" on any priced pick in Best Bets, Value Finder or a fixture to build a multi.
            </p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="betslip-legs">
              {legs.map((l) => (
                <div key={l.id} className="rounded-2xl bg-secondary/50 p-3 flex items-start gap-2" data-testid="betslip-leg">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{l.player}</div>
                    <div className="text-xs text-muted-foreground">{l.prop}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                      {l.home} v {l.away}
                    </div>
                    {l.tournament && (
                      <div className="inline-flex items-center gap-1 text-[10px] font-medium text-primary bg-primary/10 rounded-full px-1.5 py-0.5 mt-1 max-w-full">
                        <Goal className="h-2.5 w-2.5 shrink-0" />
                        <span className="truncate">{l.tournament}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-sm text-primary">@{(l.odd || 0).toFixed(2)}</div>
                    {l.prob != null && (
                      <div className="text-[11px] text-muted-foreground">{fmtProb(l.prob * 100)}</div>
                    )}
                  </div>
                  <button
                    onClick={() => removeLeg(l.id)}
                    data-testid="betslip-remove-leg"
                    className="text-muted-foreground hover:text-destructive p-1"
                    aria-label="Remove leg"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t border-border p-5 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">Stake</span>
                <div className="relative w-32">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">A$</span>
                  <Input
                    type="number"
                    min="0"
                    value={stake}
                    onChange={(e) => setStake(Math.max(0, Number(e.target.value)))}
                    data-testid="betslip-stake"
                    className="rounded-xl h-10 pl-9 text-right"
                  />
                </div>
              </div>

              <div className="rounded-2xl bg-secondary/40 p-4 space-y-2 text-sm">
                <Row label={`Combined odds (${legs.length} legs)`} value={`@${summary.combinedOdds.toFixed(2)}`} bold />
                <Row label="Model win chance" value={fmtProb(summary.combinedProb * 100)} />
                <Row label="Potential return" value={money(summary.potentialReturn)} bold />
                <Row label="Profit" value={money(summary.profit)} accent />
              </div>

              <div
                className={
                  "flex items-center justify-center gap-1.5 rounded-2xl py-2.5 text-sm font-semibold " +
                  (summary.valuePct >= 0
                    ? "bg-[#34C759]/15 text-[#34C759]"
                    : "bg-[#FF375F]/10 text-[#FF375F]")
                }
                data-testid="betslip-value"
              >
                <TrendingUp className="h-4 w-4" />
                {summary.valuePct >= 0
                  ? `+${summary.valuePct}% value vs your model`
                  : `${summary.valuePct}%, below fair value`}
              </div>

              <Button
                variant="ghost"
                onClick={clear}
                data-testid="betslip-clear"
                className="w-full rounded-xl text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" /> Clear slip
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value, bold, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={(bold ? "font-bold " : "") + (accent ? "text-[#34C759] font-bold" : "")}>{value}</span>
    </div>
  );
}
